/**
 * Lucky-friend lead ordering.
 *
 * A lucky friend makes every trade come back Lucky, so the leads worth doing
 * FIRST are the ones a Lucky copy actually completes something on. The order:
 *
 *   0. Missing as BOTH a Shiny and a Lucky — one trade fills two dexes.
 *   1. Missing as a Lucky.
 *   2. Missing as a Shiny.
 *   3. Everything else (XXL, XXS, Purified, G-Max, plain Main…).
 *
 * Legendaries lead inside each tier, then dex order, so the list is stable.
 *
 * WHAT WAS WRONG (found 2026-08-12). Tier 2 did not exist: the tiers were
 * both → lucky → EVERYTHING, so a Shiny lead ranked alongside an XXS one. The
 * mode has always been described — in its own toggle copy — as "Lucky & Shiny
 * leads", and it was delivering Lucky only. Worse in combination with the
 * legendary rule: because legendaries only lead WITHIN a tier, a legendary
 * Shiny sat below every common Lucky, which is the exact opposite of what a
 * trainer turns this toggle on to see.
 *
 * Pure and separate from the page so the tiering can be asserted directly —
 * this ordering had no test, which is how it drifted from its own description.
 */

/** The shape the ranker needs: a species id and which dex axis it surfaces. */
export interface RankableLead {
  id: number;
  variant: string;
}

export type LuckyTier = 0 | 1 | 2 | 3;

/**
 * Species that surface on BOTH the Shiny and the Lucky axis in this list.
 *
 * Computed from the rows PASSED IN, i.e. after filtering — so with the dex
 * filter pinned to Lucky there are no Shiny rows to pair with and tier 0 is
 * legitimately empty. That is correct: the trainer asked to see one axis.
 */
export function bothShinyAndLuckyIds(rows: RankableLead[]): Set<number> {
  const shinyIds = new Set(rows.filter((r) => r.variant === "shiny").map((r) => r.id));
  const both = new Set<number>();
  for (const r of rows) {
    if (r.variant === "lucky" && shinyIds.has(r.id)) both.add(r.id);
  }
  return both;
}

export function luckyTier(row: RankableLead, both: Set<number>): LuckyTier {
  if (both.has(row.id)) return 0;
  if (row.variant === "lucky") return 1;
  if (row.variant === "shiny") return 2;
  return 3;
}


/**
 * Full comparator including the legendary and dex-order tiebreaks.
 * `isLegendary` is injected so this module stays free of the dex data tables.
 */
export function luckyLeadComparator(
  both: Set<number>,
  isLegendary: (speciesId: number) => boolean,
) {
  return (a: RankableLead, b: RankableLead): number => {
    const ta = luckyTier(a, both);
    const tb = luckyTier(b, both);
    if (ta !== tb) return ta - tb;
    const aLegend = isLegendary(a.id) ? 0 : 1;
    const bLegend = isLegendary(b.id) ? 0 : 1;
    if (aLegend !== bLegend) return aLegend - bLegend;
    return a.id - b.id;
  };
}
